import { Clock } from 'lucide-react';
import { type ReactNode, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

/**
 * Ô chọn giờ — thay cho `<input type="time">`.
 *
 * ## Vì sao thay
 *
 * Cùng lý do với `<DatePicker>`: `<input type="time">` không style được phần
 * bên trong, và nó hiện AM/PM hay 24 giờ **theo ngôn ngữ hệ điều hành** chứ
 * không theo ngôn ngữ ứng dụng. Một người dùng máy đặt tiếng Anh–Mỹ sẽ thấy
 * `01:00 PM` giữa một biểu mẫu tiếng Việt, trong khi dữ liệu gửi đi vẫn là
 * `13:00`. Hai cách đọc cùng một ô, và người dùng không có cách nào chọn.
 *
 * ## Vì sao là danh sách khung giờ, không phải lịch thu nhỏ
 *
 * Cả ba chỗ dùng đều là **giờ làm việc**: ca sáng, ca chiều, nghỉ nửa buổi. Người
 * ta chọn `08:00` hay `13:30`, không ai chọn `08:07`. Một danh sách nhảy 30
 * phút là hai lần bấm cho gần như mọi trường hợp — còn ô nhập vẫn còn đó cho
 * phần trăm còn lại.
 *
 * Giá trị vào/ra là `HH:MM` 24 giờ, đúng dạng cũ của `<input type="time">` nên
 * call site không phải đổi gì.
 */
export interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  /** Phút giữa hai mục trong danh sách. Mặc định 30. */
  step?: number;
  disabled?: boolean | undefined;
  id?: string | undefined;
  className?: string | undefined;
  'aria-label'?: string | undefined;
}

export function TimePicker({
  value,
  onChange,
  step = 30,
  disabled,
  id,
  className,
  ...aria
}: TimePickerProps): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const slots = useMemo(() => {
    const out: string[] = [];
    for (let minute = 0; minute < 24 * 60; minute += step) {
      out.push(
        `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`,
      );
    }
    return out;
  }, [step]);

  /**
   * Cuộn tới giờ đang chọn ngay khi danh sách xuất hiện.
   *
   * Không có bước này thì danh sách luôn mở ở `00:00`, và người sửa ca chiều
   * phải cuộn qua mười ba tiếng đêm hôm mỗi lần.
   *
   * ⚠️ Làm bằng **ref callback**, không phải `useEffect` theo `open`. Nội dung
   * popover nằm trong một portal chỉ tồn tại khi đang mở, nên effect chạy lúc
   * `open` vừa thành `true` có thể chưa thấy node nào — kể cả khi bọc trong
   * `requestAnimationFrame`. Ref callback thì chạy **đúng lúc** node vào DOM.
   */
  const attachList = (node: HTMLDivElement | null): void => {
    listRef.current = node;
    node?.querySelector<HTMLElement>('[data-selected="true"]')?.scrollIntoView({ block: 'center' });
  };

  const commit = (text: string): void => {
    setDraft(null);
    const parsed = parseTime(text.trim());
    if (parsed !== null) onChange(parsed);
  };

  return (
    <div className={cn('relative', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          disabled={disabled}
          value={draft ?? value}
          placeholder="hh:mm"
          onFocus={() => setDraft(value)}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={(event) => commit(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit(event.currentTarget.value);
            } else if (event.key === 'Escape' && draft !== null) {
              setDraft(null);
            }
          }}
          className={cn(
            'flex h-9 w-full rounded-DEFAULT border border-input bg-background py-1 pl-3 pr-9 text-sm tabular-nums shadow-xs transition-colors',
            'placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
            'aria-invalid:border-destructive',
          )}
          {...aria}
        />

        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label={t('calendar.openTime')}
            className="absolute inset-y-0 right-1 my-auto inline-flex size-7 items-center justify-center text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50 rounded-full glass-hover"
          >
            <Clock className="size-4" aria-hidden />
          </button>
        </PopoverTrigger>

        <PopoverContent className="w-28 p-1">
          <div ref={attachList} role="listbox" className="max-h-56 overflow-y-auto">
            {slots.map((slot) => (
              <button
                key={slot}
                type="button"
                role="option"
                aria-selected={slot === value}
                data-selected={slot === value}
                onClick={() => {
                  setDraft(null);
                  onChange(slot);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center rounded-DEFAULT px-2 py-1.5 text-sm tabular-nums transition-colors hover:bg-muted',
                  slot === value &&
                    'bg-primary font-medium text-primary-foreground hover:bg-primary/90',
                )}
              >
                {slot}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Chuỗi người dùng gõ → `HH:MM`, hoặc `null` nếu không đọc được.
 *
 * Nhận `8`, `8:5`, `08:05`, `8h`, `8h30` — những cách người ta thật sự gõ giờ.
 * Trả `null` (giữ giá trị cũ) thay vì đoán khi vượt khỏi 23:59: im lặng kẹp
 * `25:00` về `23:59` là ghi một ca làm việc mà người dùng không hề nhập.
 */
export function parseTime(text: string): string | null {
  const match = /^(\d{1,2})\s*[:h.]?\s*(\d{1,2})?$/.exec(text);
  if (!match) return null;

  const hour = Number(match[1]);
  const minute = match[2] === undefined ? 0 : Number(match[2]);
  if (hour > 23 || minute > 59) return null;

  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}
